/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import {
    ValidationError,
    AttributeValueEvent,
    TranslateFunction,
    TranslationContextType,
    TRANSLATION_CONTEXT_DEFAULT,
    TranslationContext,
    ProvidersContextType,
    ProvidersContext,
    defaultAttributeValidation,
    buildIntegerAttributeValidation,
    buildFloatAttributeValidation,
    POSITIVE_FLOAT_VALIDATION,
    POSITIVE_INTEGER_VALIDATION,
    noValidate,
    buildValidateInterInMinMax,
    buildValidatePositiveGreaterThan,
    buildValidatePositiveLessThan,
    buildEnumAttributeValidation,
    getDisplayText,
    saveNewAttributeValue,
} from './commons';
import {
    EntityAttribute,
    EntityAttributeProps,
    EntityAttributeWithStaticUnitProps,
    EntityAttributeState,
    getEntityAttributeStateFromProps,
    ENTITY_ATTRIBUTE_DEFAULT_PROPS,
} from './EntityAttribute';
import {DefaultEntityAttribute} from './DefaultEntityAttribute';
import {
    BooleanEntityAttribute,
    BOOLEAN_ENTITY_ATTRIBUTE_DEFAULT_PROPS,
} from './BooleanEntityAttribute';
import {
    IntegerEntityAttribute,
    INTEGER_ENTITY_ATTRIBUTE_DEFAULT_PROPS,
} from './IntegerEntityAttribute';
import {
    FloatEntityAttribute,
    FLOAT_ENTITY_ATTRIBUTE_DEFAULT_PROPS,
} from './FloatEntityAttribute';
import {
    FloatEntityAttributeWithStaticUnit,
    FLOAT_ENTITY_ATTRIBUTE_WITH_UNIT_DEFAULT_PROPS,
} from './FloatEntityAttributeWithStaticUnit';
import {
    EnumEntityAttribute,
    EnumEntityAttributeProps,
    EnumEntityAttributeState,
    ENUM_ENTITY_ATTRIBUTE_DEFAULT_PROPS,
} from './EnumEntityAttribute';
import {
    EntityRelation,
    EntityRelationProps,
    EntityRelationState,
    ENTITY_RELATION_DEFAULT_PROPS,
} from './EntityRelation';
import {DefaultEntityRelation} from './DefaultEntityRelation';
import {
    EntityRelationsGroup,
    EntityRelationsGroupProps,
    ENTITY_RELATIONS_GROUP_DEFAULT_PROPS,
} from './EntityRelationsGroup';
import {DefaultEntityRelationsGroup} from './DefaultEntityRelationsGroup';
import {
    MergedEntityRelationsGroup,
    MergedEntityRelationsGroupProps,
    MergedEntityRelationsGroupState,
    MERGED_ENTITY_RELATIONS_GROUP_DEFAULT_PROPS,
} from './MergedEntityRelationsGroup';
import {
    EntityForm,
    EntityFormState,
    EntityFormProps,
    ENTITY_DEFAULT_PROPS,
    EntityStatus,
    getRelationGroups,
} from './EntityForm';
import {DefaultEntityForm} from './DefaultEntityForm';
import {
    EntityRelationsSpecification,
    EntityRelationsChanges,
    EntityRelationsEditorProps,
    EntityRelationsEditorState,
    EntityRelationsEditor,
    RelationStatus,
    getEntityRelationStatus,
    getRelatedCount,
} from './EntityRelationsEditor';
import {
    lookupLoadFromResults,
    lookupEntitiesByField,
    lookupEntitiesRelated,
    EntityRelationsLookupProps,
    DefaultEntityRelationsLookupProps,
    DefaultEntityRelationsLookupState,
    DEFAULT_ENTITY_RELATIONS_LOOKUP_DEFAULT_PROPS,
    DefaultEntityRelationsLookup,
    lookupEntitiesRelatedWithChanges,
    applyChanges,
} from './EntityRelationsEditorLookup';
import {
    EntityRelationsSelectorProps,
    DefaultEntityRelationsSelectorProps,
    DEFAULT_ENTITY_RELATIONS_SELECTOR_DEFAULT_PROPS,
    DefaultEntityRelationsSelector,
} from './EntityRelationsEditorSelector';
import {
    ENTITY_RELATIONS_EDITOR_DEFAULT_PROPS,
    DefaultEntityRelationsEditorProps,
    DefaultEntityRelationsEditor,
} from './EntityRelationsEditorDefault';
import {
    ENTITY_RELATIONS_EDITOR_SINGLE_PROPS,
    SingleEntityRelationsEditorProps,
    SingleEntityRelationsEditorState,
    SingleEntityRelationsEditor,
} from './EntityRelationsEditorSingle';
import {
    EntityBrowser,
    EntityBrowserProps,
    EntityBrowserState,
    DefaultEntityBrowser,
    ENTITY_BROWSER_DEFAULT_PROPS,
} from './EntityBrowser';
import {EntityTypeListElem} from './schema-navigator/EntityType';
import {EntityTypesList} from './schema-navigator/EntityTypesList';
import {RelationsAndAttributesList} from './schema-navigator/RelationsAndAttributesList';
import {SchemaNavigator} from './schema-navigator/SchemaNavigator';
import {RqlBrowser} from './RqlBrowser';
import {
    StandaloneRqlQuerier,
    StandaloneRqlQuerierProps,
} from './StandaloneRqlQuerier';
import {useQuery, RqlQueryResult} from './useQuery';
import {
    JSONQueryResults,
    TableQueryResults,
    TabbedQueryResults,
    QueryError,
} from './QueryResults';
import {QueryTextInput, QueryMonacoEditorTextInput} from './QueryInput';
import {Loading} from './Loading';
import {StandaloneSchemaNavigator} from './schema-navigator/StandaloneSchemaNavigator';
import {RqlBrowserProps, useRqlBrowser} from './useRqlBrowser';
import {
    RqlBrowserQuerier,
    RqlBrowserControlPanel,
    RqlBrowserError,
} from './RqlBrowserComponents';

export {
    ValidationError,
    AttributeValueEvent,
    TranslateFunction,
    TranslationContextType,
    TRANSLATION_CONTEXT_DEFAULT,
    TranslationContext,
    ProvidersContextType,
    ProvidersContext,
    defaultAttributeValidation,
    buildIntegerAttributeValidation,
    buildFloatAttributeValidation,
    POSITIVE_FLOAT_VALIDATION,
    POSITIVE_INTEGER_VALIDATION,
    noValidate,
    buildValidateInterInMinMax,
    buildValidatePositiveGreaterThan,
    buildValidatePositiveLessThan,
    buildEnumAttributeValidation,
    ENTITY_ATTRIBUTE_DEFAULT_PROPS,
    getDisplayText,
    saveNewAttributeValue,
    EntityAttribute,
    EntityAttributeProps,
    EntityAttributeWithStaticUnitProps,
    EntityAttributeState,
    getEntityAttributeStateFromProps,
    DefaultEntityAttribute,
    BooleanEntityAttribute,
    BOOLEAN_ENTITY_ATTRIBUTE_DEFAULT_PROPS,
    IntegerEntityAttribute,
    INTEGER_ENTITY_ATTRIBUTE_DEFAULT_PROPS,
    FloatEntityAttribute,
    FLOAT_ENTITY_ATTRIBUTE_DEFAULT_PROPS,
    FloatEntityAttributeWithStaticUnit,
    FLOAT_ENTITY_ATTRIBUTE_WITH_UNIT_DEFAULT_PROPS,
    EnumEntityAttribute,
    EnumEntityAttributeProps,
    EnumEntityAttributeState,
    ENUM_ENTITY_ATTRIBUTE_DEFAULT_PROPS,
    EntityRelation,
    EntityRelationProps,
    EntityRelationState,
    ENTITY_RELATION_DEFAULT_PROPS,
    DefaultEntityRelation,
    DefaultEntityRelationsGroup,
    EntityRelationsGroup,
    EntityRelationsGroupProps,
    ENTITY_RELATIONS_GROUP_DEFAULT_PROPS,
    MergedEntityRelationsGroup,
    MergedEntityRelationsGroupProps,
    MergedEntityRelationsGroupState,
    MERGED_ENTITY_RELATIONS_GROUP_DEFAULT_PROPS,
    EntityForm,
    EntityFormProps,
    EntityFormState,
    EntityStatus,
    ENTITY_DEFAULT_PROPS,
    getRelationGroups,
    DefaultEntityForm,
    EntityRelationsSpecification,
    lookupLoadFromResults,
    lookupEntitiesByField,
    EntityRelationsLookupProps,
    DefaultEntityRelationsLookupProps,
    DefaultEntityRelationsLookupState,
    DEFAULT_ENTITY_RELATIONS_LOOKUP_DEFAULT_PROPS,
    DefaultEntityRelationsLookup,
    EntityRelationsSelectorProps,
    DefaultEntityRelationsSelectorProps,
    DEFAULT_ENTITY_RELATIONS_SELECTOR_DEFAULT_PROPS,
    DefaultEntityRelationsSelector,
    EntityRelationsChanges,
    applyChanges,
    lookupEntitiesRelatedWithChanges,
    EntityRelationsEditorProps,
    EntityRelationsEditorState,
    EntityRelationsEditor,
    RelationStatus,
    getEntityRelationStatus,
    getRelatedCount,
    lookupEntitiesRelated,
    ENTITY_RELATIONS_EDITOR_DEFAULT_PROPS,
    DefaultEntityRelationsEditorProps,
    DefaultEntityRelationsEditor,
    ENTITY_RELATIONS_EDITOR_SINGLE_PROPS,
    SingleEntityRelationsEditorProps,
    SingleEntityRelationsEditorState,
    SingleEntityRelationsEditor,
    EntityBrowser,
    EntityBrowserProps,
    EntityBrowserState,
    DefaultEntityBrowser,
    ENTITY_BROWSER_DEFAULT_PROPS,
    EntityTypeListElem,
    EntityTypesList,
    RelationsAndAttributesList,
    SchemaNavigator,
    StandaloneSchemaNavigator,
    useRqlBrowser,
    RqlBrowser,
    RqlBrowserProps,
    RqlBrowserQuerier,
    RqlBrowserControlPanel,
    RqlBrowserError,
    JSONQueryResults,
    TableQueryResults,
    TabbedQueryResults,
    QueryError,
    QueryTextInput,
    QueryMonacoEditorTextInput,
    StandaloneRqlQuerier,
    StandaloneRqlQuerierProps,
    useQuery,
    RqlQueryResult,
    Loading,
};
